import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Users, UserCheck, LogOut, Clock as ClockIcon, Ban } from 'lucide-react';
import { cn } from '../../../utils/cn';

export type VisitorStatusFilter = 'all' | 'inside' | 'awaiting' | 'exited' | 'expired';

interface StatusFilterDropdownProps {
  value: VisitorStatusFilter;
  onChange: (value: VisitorStatusFilter) => void;
  counts?: {
    all: number;
    inside: number;
    awaiting: number;
    exited: number;
    expired: number;
  };
  className?: string;
  lightTheme?: boolean;
}

const filterOptions = [
  { value: 'all', label: 'Todas as pessoas', icon: Users, color: 'text-gray-400' },
  { value: 'inside', label: 'Dentro', icon: UserCheck, color: 'text-green-500' },
  { value: 'awaiting', label: 'Aguardando entrada', icon: ClockIcon, color: 'text-yellow-500' },
  { value: 'exited', label: 'Saíram hoje', icon: LogOut, color: 'text-gray-500' },
  { value: 'expired', label: 'Expirados', icon: Ban, color: 'text-red-500' },
] as const;

export const StatusFilterDropdown: React.FC<StatusFilterDropdownProps> = ({
  value,
  onChange,
  counts,
  className,
  lightTheme = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced close — gives the mouse time to cross the gap between button and dropdown
  const scheduleClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 150);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    setIsOpen(true);
  }, []);

  const currentOption = filterOptions.find(opt => opt.value === value) || filterOptions[0];
  const Icon = currentOption.icon;

  return (
    <div
      ref={dropdownRef}
      className={cn("relative", className)}
      onMouseLeave={scheduleClose}
    >
      {/* Dropdown trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={cancelClose}
        className={cn(
          "flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors text-sm",
          lightTheme
            ? "bg-white border-gray-300 hover:bg-gray-50"
            : "bg-gray-800 border-gray-700 hover:bg-gray-750"
        )}
      >
        <Icon className={cn("h-4 w-4", currentOption.color)} />
        <span className={cn("font-medium", lightTheme ? "text-gray-700" : "text-gray-300")}>{currentOption.label}</span>
        {counts && counts[value] > 0 && (
          <span className={cn("ml-1 px-1.5 py-0.5 rounded text-xs font-semibold",
            lightTheme ? "bg-gray-200 text-gray-700" : "bg-gray-700 text-gray-300"
          )}>
            {counts[value]}
          </span>
        )}
        <ChevronDown className={cn(
          "h-5 w-5 transition-transform",
          lightTheme ? "text-gray-500" : "text-gray-400",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          data-dropdown="true"
          className={cn(
            "absolute top-full right-0 mt-1 w-56 border rounded-lg shadow-2xl overflow-hidden z-[9999]",
            lightTheme ? "bg-white border-gray-200" : "bg-gray-900 border-gray-700"
          )}
          onMouseEnter={cancelClose}
        >
          {filterOptions.map((option) => {
            const OptionIcon = option.icon;
            const isActive = option.value === value;
            const count = counts?.[option.value] || 0;

            return (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                  isActive
                    ? lightTheme ? "bg-blue-50 text-blue-700" : "bg-primary-600/20 text-white"
                    : lightTheme ? "text-gray-700 hover:bg-gray-50" : "text-gray-300 hover:bg-gray-800"
                )}
              >
                <OptionIcon className={cn("h-5 w-5", option.color)} />
                <span className="flex-1 text-left">{option.label}</span>
                {count > 0 && (
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-xs font-semibold",
                    isActive
                      ? lightTheme ? "bg-blue-100 text-blue-700" : "bg-blue-600 text-white"
                      : lightTheme ? "bg-gray-200 text-gray-700" : "bg-gray-700 text-gray-300"
                  )}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
